/**
 * API Response Handler Utility
 *
 * This utility provides standardized handling for all backend API responses
 * based on the documented API response formats.
 */

/**
 * Standard API Response Format
 * @typedef {Object} ApiResponse
 * @property {boolean} success - Whether the request was successful
 * @property {string} message - Response message
 * @property {*} data - Response payload (optional)
 * @property {string} error - Error type (optional)
 * @property {Object} details - Additional error context (optional)
 */

/**
 * Handle API response and extract data
 * @param {Object} response - Axios response object
 * @returns {Object} - Standardized response object
 */
export const handleApiResponse = (response) => {
  const {data, status, statusText} = response;

  // Check if response has the expected format
  if (data && typeof data === 'object') {
    return {
      success: data.success || false,
      message: data.message || statusText || 'Request completed',
      data: data.data || null,
      error: data.error || null,
      details: data.details || null,
      status: status,
      originalResponse: data,
    };
  }

  // Fallback for unexpected response format
  return {
    success: status >= 200 && status < 300,
    message: statusText || 'Request completed',
    data: data,
    error: null,
    details: null,
    status: status,
    originalResponse: data,
  };
};

/**
 * Handle API error response
 * @param {Object} error - Axios error object
 * @returns {Object} - Standardized error response
 */
export const handleApiError = (error) => {
  const {response, message} = error;

  // Network error
  if (!response) {
    return {
      success: false,
      message: 'Network error - Unable to connect to server',
      error: 'NETWORK_ERROR',
      details: {originalError: message},
      status: 0,
    };
  }

  // Server responded with error status
  const {data, status} = response;

  if (data && typeof data === 'object') {
    return {
      success: false,
      message: data.message || `HTTP ${status} Error`,
      error: data.error || 'SERVER_ERROR',
      details: data.details || null,
      status: status,
      originalResponse: data,
    };
  }

  // Fallback for unexpected error format
  return {
    success: false,
    message: `HTTP ${status} Error`,
    error: 'HTTP_ERROR',
    details: {status, statusText: response.statusText},
    status: status,
  };
};

/**
 * Extract data from successful response
 * @param {Object} response - Standardized response object
 * @returns {*} - Extracted data or null
 */
export const extractData = (response) => {
  if (!response.success) {
    return null;
  }

  return response.data;
};

/**
 * Extract message from response
 * @param {Object} response - Standardized response object
 * @returns {string} - Response message
 */
export const extractMessage = (response) => {
  return response.message || 'No message provided';
};

/**
 * Check if response is successful
 * @param {Object} response - Standardized response object
 * @returns {boolean} - Whether the response is successful
 */
export const isSuccess = (response) => {
  return response.success === true;
};

/**
 * Check if response has error
 * @param {Object} response - Standardized response object
 * @returns {boolean} - Whether the response has an error
 */
export const hasError = (response) => {
  return response.success === false || response.error !== null;
};

/**
 * Get error message from response
 * @param {Object} response - Standardized response object
 * @returns {string} - Error message
 */
export const getErrorMessage = (response) => {
  if (response.success) {
    return null;
  }

  return response.message || 'Unknown error occurred';
};

/**
 * Get error type from response
 * @param {Object} response - Standardized response object
 * @returns {string} - Error type
 */
export const getErrorType = (response) => {
  return response.error || 'UNKNOWN_ERROR';
};

/**
 * Handle WhatsApp API responses specifically
 * @param {Object} response - Axios response object
 * @returns {Object} - Standardized WhatsApp response
 */
export const handleWhatsAppResponse = (response) => {
  const standardized = handleApiResponse(response);

  if (standardized.success && standardized.data) {
    // Handle different WhatsApp response formats
    if (standardized.data.accounts) {
      // Status response format
      return {
        ...standardized,
        data: {
          connected: standardized.data.accounts.primary?.isConnected || false,
          status:
            standardized.data.accounts.primary?.status?.message ||
            'Unknown status',
          phoneNumber: standardized.data.accounts.primary?.phoneNumber || null,
          lastSeen: standardized.data.accounts.primary?.lastSeen || null,
        },
      };
    }

    if (standardized.data.qrCode) {
      // Connect response format
      return {
        ...standardized,
        data: {
          qrCode: standardized.data.qrCode,
          expiresIn: standardized.data.expiresIn || 300,
        },
      };
    }

    if (standardized.data.registered !== undefined) {
      // Check number response format
      return {
        ...standardized,
        data: {
          phoneNumber: standardized.data.phoneNumber,
          registered: standardized.data.registered,
          whatsappId: standardized.data.whatsappId || null,
          contactName: standardized.data.contactName || null,
        },
      };
    }
  }

  return standardized;
};

/**
 * Handle message API responses specifically
 * @param {Object} response - Axios response object
 * @returns {Object} - Standardized message response
 */
export const handleMessageResponse = (response) => {
  const standardized = handleApiResponse(response);

  if (standardized.success && standardized.data) {
    // Handle message response formats
    return {
      ...standardized,
      data: {
        totalMessages: standardized.data.totalMessages || 0,
        successCount: standardized.data.successCount || 0,
        failedCount: standardized.data.failedCount || 0,
        failedMessages: standardized.data.failedMessages || [],
        recipient: standardized.data.recipient || null,
        messageId: standardized.data.messageId || null,
        status: standardized.data.status || 'unknown',
      },
    };
  }

  return standardized;
};

/**
 * Handle customer API responses specifically
 * @param {Object} response - Axios response object
 * @returns {Object} - Standardized customer response
 */
export const handleCustomerResponse = (response) => {
  const standardized = handleApiResponse(response);

  if (standardized.success && standardized.data) {
    // Handle customer response formats
    if (Array.isArray(standardized.data)) {
      // Array of customers
      return {
        ...standardized,
        data: {
          customers: standardized.data,
          total: standardized.data.length,
          page: 0,
          limit: standardized.data.length,
        },
      };
    }

    if (standardized.data.customers) {
      // Paginated customers response
      return standardized;
    }

    // Single customer response
    return standardized;
  }

  return standardized;
};

/**
 * Handle history API responses specifically
 * @param {Object} response - Axios response object
 * @returns {Object} - Standardized history response
 */
export const handleHistoryResponse = (response) => {
  const standardized = handleApiResponse(response);

  if (standardized.success && standardized.data) {
    // Handle history response formats
    return {
      ...standardized,
      data: {
        totalMessages: standardized.data.totalMessages || 0,
        totalHistory: standardized.data.totalHistory || 0,
        reports: standardized.data.reports || [],
        pagination: standardized.data.pagination || {
          page: 0,
          limit: 10,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        },
      },
    };
  }

  return standardized;
};

/**
 * Create a standardized error response
 * @param {string} message - Error message
 * @param {string} errorType - Error type
 * @param {Object} details - Additional error details
 * @returns {Object} - Standardized error response
 */
export const createErrorResponse = (
  message,
  errorType = 'UNKNOWN_ERROR',
  details = null,
) => {
  return {
    success: false,
    message: message,
    error: errorType,
    details: details,
    status: 0,
  };
};

/**
 * Create a standardized success response
 * @param {*} data - Response data
 * @param {string} message - Success message
 * @returns {Object} - Standardized success response
 */
export const createSuccessResponse = (
  data = null,
  message = 'Operation completed successfully',
) => {
  return {
    success: true,
    message: message,
    data: data,
    error: null,
    details: null,
    status: 200,
  };
};

/**
 * Validate API response format
 * @param {Object} response - Response object to validate
 * @returns {boolean} - Whether the response has valid format
 */
export const validateResponse = (response) => {
  return (
    response &&
    typeof response === 'object' &&
    typeof response.success === 'boolean' &&
    typeof response.message === 'string'
  );
};

export default {
  handleApiResponse,
  handleApiError,
  extractData,
  extractMessage,
  isSuccess,
  hasError,
  getErrorMessage,
  getErrorType,
  handleWhatsAppResponse,
  handleMessageResponse,
  handleCustomerResponse,
  handleHistoryResponse,
  createErrorResponse,
  createSuccessResponse,
  validateResponse,
};
